import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BadgeCheck, MapPin, Users, UserPlus } from "lucide-react";
import { MainLayout } from "@/layouts/MainLayout";
import { useMockQuery } from "@/hooks/useMockQuery";
import { apiClient } from "@/lib/apiClient";
import { EventCard } from "@/components/cards/EventCard";
import { LiveCard } from "@/components/cards/LiveCard";
import { EmptyState } from "@/components/states/EmptyState";
import { ErrorState } from "@/components/states/ErrorState";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);

function ProfileSkeleton() {
  return (
    <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8">
      <div className="flex flex-col items-center gap-4">
        <Skeleton className="h-28 w-28 rounded-full" />
        <Skeleton className="h-8 w-48 rounded-lg" />
        <Skeleton className="h-4 w-72 rounded" />
      </div>
    </div>
  );
}

export default function Profile() {
  const { username } = useParams();
  const [following, setFollowing] = useState(false);

  const { data: user, loading, error, refetch } = useMockQuery(
    () => apiClient.getUser(username),
    [username]
  );

  const content = useMockQuery(
    () => (user ? apiClient.getUserContent(user.id) : Promise.resolve(null)),
    [user?.id]
  );

  if (loading)
    return (
      <MainLayout>
        <ProfileSkeleton />
      </MainLayout>
    );

  if (error)
    return (
      <MainLayout>
        <div className="mx-auto max-w-3xl px-5 py-24">
          <ErrorState
            title="Profile not found"
            description="We couldn't find this creator."
            error={error}
            onRetry={refetch}
          />
        </div>
      </MainLayout>
    );

  return (
    <MainLayout>
      {/* Banner */}
      <div className="h-40 w-full bg-gradient-to-r from-brand to-blue-400 sm:h-52" />
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <div className="-mt-16 flex flex-col items-start gap-5 sm:-mt-20 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-end">
            <Avatar className="h-28 w-28 border-4 border-background shadow-md sm:h-32 sm:w-32">
              <AvatarImage src={user.avatar} alt={user.displayName} />
              <AvatarFallback>{user.displayName[0]}</AvatarFallback>
            </Avatar>
            <div className="pb-2">
              <h1 className="flex items-center gap-2 font-heading text-3xl font-extrabold tracking-tight text-foreground">
                {user.displayName}
                {user.verified && <BadgeCheck className="h-6 w-6 text-brand" />}
              </h1>
              <p className="text-muted-foreground">@{user.username}</p>
            </div>
          </div>
          <Button
            data-testid="profile-follow-btn"
            onClick={() => setFollowing((v) => !v)}
            variant={following ? "outline" : "default"}
            className={
              following
                ? "rounded-full"
                : "rounded-full bg-brand px-6 hover:bg-brand-hover"
            }
          >
            <UserPlus className="mr-2 h-4 w-4" />
            {following ? "Following" : "Follow"}
          </Button>
        </div>

        <div className="mt-6 max-w-2xl">
          <p className="text-foreground">{user.bio}</p>
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <MapPin className="h-4 w-4" /> {user.location}
            </span>
            <span className="flex items-center gap-1.5">
              <Users className="h-4 w-4" />
              <strong className="text-foreground">{fmt(user.followers)}</strong>{" "}
              followers
            </span>
            <span>
              <strong className="text-foreground">{fmt(user.following)}</strong>{" "}
              following
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {user.tags.map((t) => (
              <span
                key={t}
                className="rounded-full bg-secondary px-3 py-1 text-sm text-muted-foreground"
              >
                {t}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-10 pb-16">
          <Tabs defaultValue="events">
            <TabsList className="mb-8 rounded-full bg-secondary p-1">
              <TabsTrigger value="events" data-testid="profile-tab-events" className="rounded-full px-6 data-[state=active]:bg-white data-[state=active]:text-brand data-[state=active]:shadow-sm">
                Events
              </TabsTrigger>
              <TabsTrigger value="live" data-testid="profile-tab-live" className="rounded-full px-6 data-[state=active]:bg-white data-[state=active]:text-brand data-[state=active]:shadow-sm">
                Live
              </TabsTrigger>
            </TabsList>

            <TabsContent value="events">
              {content.loading ? (
                <Skeleton className="h-40 w-full rounded-2xl" />
              ) : content.data?.events?.length ? (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {content.data.events.map((e) => (
                    <EventCard key={e.id} event={e} />
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="No events yet"
                  description={`${user.displayName} hasn't hosted any events yet.`}
                />
              )}
            </TabsContent>

            <TabsContent value="live">
              {content.loading ? (
                <Skeleton className="h-40 w-full rounded-2xl" />
              ) : content.data?.lives?.length ? (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {content.data.lives.map((s) => (
                    <LiveCard key={s.id} stream={s} />
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="Not live right now"
                  description={`Follow to get notified when ${user.displayName} goes live.`}
                />
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </MainLayout>
  );
}
